import { __decorate } from "tslib";
import { ArrayDecoder, AutoEncoder, StringDecoder, field } from '@common/encoding';
import { v4 as uuidv4 } from 'uuid';
import { BundleDiscount } from './BundleDiscount.js';
import { Group, GroupStatus } from './Group.js';
import { GroupCategory, GroupCategorySettings, GroupCategoryTree } from './GroupCategory.js';
import { GroupType } from './GroupType.js';
import { RegistrationPeriodBase } from './RegistrationPeriodBase.js';
import { SetupSteps } from './SetupSteps.js';
export class RegistrationPeriodSettings extends AutoEncoder {
}
export class RegistrationPeriod extends RegistrationPeriodBase {
    settings = RegistrationPeriodSettings.create({});
}
__decorate([
    field({ decoder: RegistrationPeriodSettings })
], RegistrationPeriod.prototype, "settings", void 0);
export class OrganizationRegistrationPeriodSettings extends AutoEncoder {
    /**
     * All the available categories
     */
    categories = [GroupCategory.create({ id: 'root' })]; // we use ID root here because this ID needs to stay the same since it won't be saved
    /**
     * We use one invisible root category to simplify the difference between non-root and root category
     */
    rootCategoryId = this.categories[0]?.id ?? '';
    get rootCategory() {
        return this.categories.find(c => c.id === this.rootCategoryId);
    }
    bundleDiscounts = [];
}
__decorate([
    field({ decoder: new ArrayDecoder(GroupCategory) })
], OrganizationRegistrationPeriodSettings.prototype, "categories", void 0);
__decorate([
    field({ decoder: StringDecoder })
], OrganizationRegistrationPeriodSettings.prototype, "rootCategoryId", void 0);
__decorate([
    field({ decoder: new ArrayDecoder(BundleDiscount), version: 374 })
], OrganizationRegistrationPeriodSettings.prototype, "bundleDiscounts", void 0);
export class OrganizationRegistrationPeriod extends AutoEncoder {
    id;
    period;
    settings = OrganizationRegistrationPeriodSettings.create({});
    groups = [];
    setupSteps = SetupSteps.create({});
    getDiffName() {
        return this.period.name;
    }
    getDiffValue() {
        return this.period.name;
    }
    /**
     * Get all groups that are in a category
     */
    get availableCategories() {
        return this.adminCategoryTree.getAllCategories();
    }
    /**
     * (todo) Contains the fully build hierarchy without the need for ID lookups. Try not to use this tree when modifying it.
     */
    get categoryTree() {
        return this.getCategoryTree();
    }
    get publicCategoryTree() {
        return this.getCategoryTree({
            smartCombine: true,
            maxDepth: 2,
            // every group should have at least 1 visible price
            filterGroups: g => g.settings.prices.some(p => !p.hidden),
        });
    }
    get adminCategoryTree() {
        return this.getCategoryTree({ admin: true });
    }
    get waitingLists() {
        return this.groups.filter(g => g.type === GroupType.WaitingList);
    }
    get rootCategory() {
        return this.settings.categories.find(c => c.id === this.settings.rootCategoryId);
    }
    /**
     * Contains the fully build hierarchy without the need for ID lookups. Try not to use this tree when modifying it.
     *
     * For registration members perspective, try to use options.admin instead of options.permissions.
     * options.permissions is only used if you want to hide groups and empty categories that you don't have permissions for.
     */
    getCategoryTree(options) {
        const root = this.rootCategory;
        if (root) {
            let tree = GroupCategoryTree.build(root, this, {
                groups: options?.filterGroups ? this.groups.filter(options.filterGroups) : undefined,
                permissions: options?.permissions,
                maxDepth: options?.maxDepth,
                smartCombine: options?.smartCombine,
            });
            if (!options?.permissions) {
                // Hide non public items
                tree = tree.filterForDisplay(options?.admin ?? false, options?.smartCombine);
            }
            if (tree.categories.length == 0 && tree.groups.length > 0) {
                tree.settings.name = $t(`%aT`);
                return GroupCategoryTree.create({
                    settings: GroupCategorySettings.create({
                        name: '',
                    }),
                    categories: [tree],
                });
            }
            const usedGroups = tree.getAllGroups();
            const unusedGroups = this.groups.filter(g => !usedGroups.includes(g));
            if (unusedGroups.length > 0) {
                console.warn('Unused groups found in category tree');
            }
            return tree;
        }
        // Broken setup here
        console.warn('Root category ID is missing in categories. Migration might be needed');
        return GroupCategoryTree.create({});
    }
    duplicate(newPeriod) {
        // Create new groups + map old to new groups
        const groupMap = new Map();
        const categoryMap = new Map();
        const newOrganizationPeriod = OrganizationRegistrationPeriod.create({
            period: newPeriod,
        });
        // Give each duplicated bundle discount a new id and remap the references on group prices
        const discountMap = new Map();
        newOrganizationPeriod.settings.bundleDiscounts = this.settings.bundleDiscounts.map((discount) => {
            const newDiscount = discount.clone();
            newDiscount.id = uuidv4();
            discountMap.set(discount.id, newDiscount.id);
            return newDiscount;
        });
        const yearDifference = newPeriod.startDate.getFullYear() - this.period.startDate.getFullYear();
        for (const group of this.groups) {
            const newGroup = Group.create({
                ...group,
                id: undefined,
                periodId: newPeriod.id,
                settings: group.settings.clone(),
            });
            // Increase all dates with exactly one year
            if (newGroup.settings.registrationStartDate) {
                newGroup.settings.registrationStartDate.setFullYear(newGroup.settings.registrationStartDate.getFullYear() + yearDifference);
            }
            if (newGroup.settings.registrationEndDate) {
                newGroup.settings.registrationEndDate.setFullYear(newGroup.settings.registrationEndDate.getFullYear() + yearDifference);
            }
            for (const price of newGroup.settings.prices) {
                if (price.startDate) {
                    price.startDate.setFullYear(price.startDate.getFullYear() + yearDifference);
                }
                if (price.endDate) {
                    price.endDate.setFullYear(price.endDate.getFullYear() + yearDifference);
                }
                // Remap the bundle discount references to the newly generated discount ids
                price.bundleDiscounts = new Map([...price.bundleDiscounts].map(([discountId, settings]) => [discountMap.get(discountId) ?? discountId, settings]));
            }
            // Force close
            if (group.type !== GroupType.WaitingList) {
                newGroup.status = GroupStatus.Closed;
            }
            groupMap.set(group.id, newGroup.id);
            newOrganizationPeriod.groups.push(newGroup);
        }
        // Fix waiting list ids
        for (const group of newOrganizationPeriod.groups) {
            if (group.waitingList) {
                const newId = groupMap.get(group.waitingList.id) ?? null;
                if (newId) {
                    group.waitingList = newOrganizationPeriod.groups.find(g => g.id === newId) ?? null;
                }
                else {
                    group.waitingList = null;
                }
            }
        }
        for (const category of this.settings.categories) {
            const newCategory = category.clone();
            newCategory.id = uuidv4();
            newCategory.groupIds = category.groupIds.map(groupId => groupMap.get(groupId)).filter(id => id);
            categoryMap.set(category.id, newCategory.id);
            newOrganizationPeriod.settings.categories.push(newCategory);
        }
        // Update category ids
        for (const category of newOrganizationPeriod.settings.categories) {
            category.categoryIds = category.categoryIds.map(categoryId => categoryMap.get(categoryId)).filter(id => id);
        }
        // Update root category id
        newOrganizationPeriod.settings.rootCategoryId = categoryMap.get(this.settings.rootCategoryId);
        return newOrganizationPeriod;
    }
}
__decorate([
    field({ decoder: StringDecoder, defaultValue: () => uuidv4() })
], OrganizationRegistrationPeriod.prototype, "id", void 0);
__decorate([
    field({ decoder: RegistrationPeriod })
], OrganizationRegistrationPeriod.prototype, "period", void 0);
__decorate([
    field({ decoder: OrganizationRegistrationPeriodSettings })
], OrganizationRegistrationPeriod.prototype, "settings", void 0);
__decorate([
    field({ decoder: new ArrayDecoder(Group) })
], OrganizationRegistrationPeriod.prototype, "groups", void 0);
__decorate([
    field({ decoder: SetupSteps, version: 324 })
], OrganizationRegistrationPeriod.prototype, "setupSteps", void 0);
export class RegistrationPeriodList extends AutoEncoder {
    organizationPeriods = [];
    periods = [];
}
__decorate([
    field({ decoder: new ArrayDecoder(OrganizationRegistrationPeriod) })
], RegistrationPeriodList.prototype, "organizationPeriods", void 0);
__decorate([
    field({ decoder: new ArrayDecoder(RegistrationPeriod) })
], RegistrationPeriodList.prototype, "periods", void 0);
//# sourceMappingURL=RegistrationPeriod.js.map